import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, API } from "@/lib/api";
import { ChevronDown, ChevronRight, Users, Award, Search, Trophy } from "lucide-react";

const ABS = (u) => (u && u.startsWith("/api/") ? `${API.replace(/\/api$/, "")}${u}` : u);

export default function AdminReferralTree() {
  const [tree, setTree] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/admin/referrals/tree").then((r) => setTree(r.data || [])).catch(() => {});
  }, []);

  const toggle = (id) => setExpanded((s) => ({ ...s, [id]: !s[id] }));

  const filtered = tree.filter((v) =>
    !q || v.name?.toLowerCase().includes(q.toLowerCase()) || v.email?.toLowerCase().includes(q.toLowerCase()) || v.referral_code?.toLowerCase().includes(q.toLowerCase())
  );

  const totalReferred = tree.reduce((sum, v) => sum + (v.referred_count || 0), 0);
  const topVolunteer = tree[0];

  return (
    <DashboardLayout>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading text-2xl font-bold inline-flex items-center gap-2">
            <Award size={22} className="text-[#F59E0B]" />
            Referral Tree
          </h1>
          <p className="text-sm text-slate-500 mt-1">Volunteers who referred students or other volunteers, ranked by impact.</p>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-3 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search volunteer or code..."
            className="pl-9 pr-4 py-2.5 rounded-lg border border-slate-300 text-sm bg-white min-w-[280px]"
            data-testid="referral-search"
          />
        </div>
      </div>

      <div className="mt-5 grid grid-cols-3 gap-px bg-slate-200 rounded-lg overflow-hidden border border-slate-200">
        <Stat icon={Users} value={tree.length} label="Active Volunteers" />
        <Stat icon={Trophy} value={totalReferred} label="Total Referrals" />
        <Stat icon={Award} value={topVolunteer?.name || "—"} label={topVolunteer ? `Top: ${topVolunteer.referred_count} referrals` : "Top Referrer"} small />
      </div>

      <div className="mt-6 space-y-3">
        {filtered.map((v) => {
          const isOpen = !!expanded[v.id];
          return (
            <div key={v.id} className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={`referral-vol-${v.id}`}>
              <button onClick={() => toggle(v.id)} className="w-full px-5 py-4 flex items-center gap-4 hover:bg-slate-50 text-left">
                <div className="w-11 h-11 rounded-full overflow-hidden bg-slate-100 border border-slate-200 shrink-0">
                  {v.photo_url ? (
                    <img src={ABS(v.photo_url)} alt={v.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center font-black text-slate-400">
                      {(v.name || "?").charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-slate-900">{v.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span>{v.email}</span>
                    <span className="text-slate-300">·</span>
                    <span className="font-mono font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-100">{v.referral_code}</span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="heading text-2xl font-black tabular-nums">{v.referred_count}</div>
                  <div className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-500">referrals</div>
                </div>
                {isOpen ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
              </button>
              {isOpen && v.referred?.length > 0 && (
                <div className="border-t border-slate-100 bg-slate-50 divide-y divide-slate-100">
                  {v.referred.map((r) => (
                    <div key={r.id} className="px-5 py-3 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full overflow-hidden bg-white border border-slate-200 shrink-0">
                        {r.photo_url ? <img src={ABS(r.photo_url)} alt={r.name} className="w-full h-full object-cover" /> : (
                          <div className="w-full h-full flex items-center justify-center text-xs font-bold text-slate-400">{(r.name || "?").charAt(0).toUpperCase()}</div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold">{r.name}</div>
                        <div className="text-xs text-slate-500">{r.email}</div>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase font-bold tracking-wider ${r.role === "volunteer" ? "bg-violet-50 text-violet-700" : "bg-blue-50 text-brand"}`}>{r.role}</span>
                      <span className="text-xs text-slate-400">{new Date(r.created_at).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              )}
              {isOpen && v.referred?.length === 0 && (
                <div className="px-5 py-4 text-center text-xs text-slate-400 border-t border-slate-100 bg-slate-50">No referrals yet.</div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="p-10 text-center text-slate-500 border border-dashed border-slate-300 rounded-xl">
            No volunteers found.
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function Stat({ icon: Icon, value, label, small }) {
  return (
    <div className="bg-white px-5 py-4">
      <Icon size={16} className="text-slate-400" />
      <div className={`heading font-black mt-2 tabular-nums leading-none ${small ? "text-base truncate" : "text-3xl"}`}>{value}</div>
      <div className="text-[10px] font-black uppercase tracking-[0.15em] text-slate-500 mt-1">{label}</div>
    </div>
  );
}
